import { Fragment } from 'react';
import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { EditorBlockTranslationReference } from 'components/editor';
import { EditorBlockVideoLink } from '.';

import type { BlockVideo } from 'shared-types';

type Links = BlockVideo['locales'][string]['links'];
type Localizations = BlockVideo['locales'][string]['translatable'];

interface Props {
  getCurrentTime: () => number;
  links: Links;
  localizations: Localizations;
  onLinksChange: (links: Links, localizations: Localizations) => void;
  translationOnly: boolean;
  translationReference: Localizations;
}

const EditorBlockVideoLinks = ({
  getCurrentTime,
  links,
  localizations,
  onLinksChange,
  translationOnly,
  translationReference
}: Props) => {
  const { messages, theme } = useReduxState();

  const getTimeInSeconds = (timeString: string) => {
    // get time in seconds from time string, example: '04:38' => 278
    const values = timeString.split(':').map((x) => parseInt(x.trim(), 10));
    return values[0] * 60 + values[1];
  };

  const getTimeString = (time: number) => {
    // get time string from seconds, example: 278.56 => '04:38'
    const minutes = Math.floor(time / 60);
    const minutesZeroPad = minutes < 10 ? `0${minutes}` : minutes;
    const seconds = Math.floor(time - minutes * 60);
    const secondsZeroPad = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutesZeroPad}:${secondsZeroPad}`;
  };

  const getLinksCopy = () => {
    return JSON.parse(JSON.stringify(links));
  };

  const handleAddLink: React.MouseEventHandler<HTMLButtonElement> = () => {
    const currentTime = getCurrentTime();

    // ensure that time is greater than previous sibling
    let timeString = getTimeString(currentTime);
    if (links.length) {
      const prevTime = getTimeInSeconds(links[links.length - 1].time);
      if (prevTime >= currentTime) timeString = getTimeString(prevTime + 1);
    }

    const newLinks = getLinksCopy();
    newLinks.push({
      time: timeString,
      title: `{{ locales.translatable.link_${newLinks.length}_title }}`
    });
    onLinksChange(newLinks, {
      ...localizations,
      [`link_${newLinks.length}_title`]: ''
    });
  };

  const handleAddSublink: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
    const index = parseInt(ev.currentTarget.getAttribute('data-index'), 10);

    const currentTime = getCurrentTime();

    // ensure that time is greater than previous sibling or parent
    let timeString = getTimeString(currentTime);
    let prevTime = getTimeInSeconds(links[index].time);
    const sublinks = links[index].sublinks || [];
    if (sublinks.length) {
      prevTime = getTimeInSeconds(sublinks[sublinks.length - 1].time);
    }
    if (prevTime >= currentTime) timeString = getTimeString(prevTime + 1);

    const newLinks = getLinksCopy();

    const sublinkTitle = `link_${index}_sublink_${
      newLinks[index].sublinks?.length || 0
    }_title`;

    newLinks[index].sublinks = [
      ...(newLinks[index].sublinks || []),
      { time: timeString, title: `{{ locales.translatable.${sublinkTitle} }}` }
    ];

    onLinksChange(newLinks, { ...localizations, [sublinkTitle]: '' });
  };

  const handleDelete = (index: number, subIndex?: number) => {
    const newLinks = getLinksCopy();

    let localizationKey = '';
    if (subIndex !== undefined) {
      localizationKey = `link_${index}_sublink_${subIndex}_title`;
      newLinks[index].sublinks.splice(subIndex, 1);
    } else {
      localizationKey = `link_${index}_title`;
      newLinks.splice(index, 1);
    }

    const newLocalizations = { ...localizations };
    delete newLocalizations[localizationKey];

    onLinksChange(newLinks, newLocalizations);
  };

  const handleTimeChange = (
    value: string,
    index: number,
    subIndex?: number
  ) => {
    const newLinks = getLinksCopy();
    if (subIndex !== undefined) {
      newLinks[index].sublinks[subIndex].time = value;
    } else {
      newLinks[index].time = value;
    }

    onLinksChange(newLinks, localizations);
  };

  const handleTitleChange = (
    value: string,
    index: number,
    subIndex?: number
  ) => {
    const newLocalizations = { ...localizations };
    if (subIndex !== undefined) {
      newLocalizations[`link_${index}_sublink_${subIndex}_title`] = value;
    } else {
      newLocalizations[`link_${index}_title`] = value;
    }

    onLinksChange(links, newLocalizations);
  };

  return (
    <>
      <div className="title">{messages.editorBlockVideoLinks}</div>

      <div>
        <div className="links">
          {links.map((link, index) => (
            <div key={index}>
              <EditorBlockTranslationReference
                backgroundColor={theme.lightGrey}
                isEnabled={translationOnly}
                isInnerElement={true}
                referenceHtml={translationReference[`link_${index}_title`]}
                translationHtml={localizations[`link_${index}_title`]}
              >
                <EditorBlockVideoLink
                  index={index}
                  link={{
                    time: link.time,
                    title: localizations[`link_${index}_title`]
                  }}
                  onDelete={handleDelete}
                  onTimeChange={handleTimeChange}
                  onTitleChange={handleTitleChange}
                  translationOnly={translationOnly}
                />
              </EditorBlockTranslationReference>

              <div className="sublinks">
                {link.sublinks?.length
                  ? link.sublinks.map((sublink, subIndex) => (
                      <Fragment key={subIndex}>
                        <EditorBlockTranslationReference
                          isEnabled={translationOnly}
                          isInnerElement={true}
                          referenceHtml={
                            translationReference[
                              `link_${index}_sublink_${subIndex}_title`
                            ]
                          }
                          translationHtml={
                            localizations[
                              `link_${index}_sublink_${subIndex}_title`
                            ]
                          }
                        >
                          <EditorBlockVideoLink
                            index={index}
                            link={{
                              time: sublink.time,
                              title:
                                localizations[
                                  `link_${index}_sublink_${subIndex}_title`
                                ]
                            }}
                            onDelete={handleDelete}
                            onTimeChange={handleTimeChange}
                            onTitleChange={handleTitleChange}
                            subIndex={subIndex}
                            translationOnly={translationOnly}
                          />
                        </EditorBlockTranslationReference>
                      </Fragment>
                    ))
                  : null}

                {!translationOnly ? (
                  <div className="add-sublink">
                    <Button
                      category="light"
                      data-index={index}
                      icon="plus"
                      onClick={handleAddSublink}
                      size="small"
                      text={messages.editorBlockVideoAddSublink}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {!translationOnly ? (
            <div className="add-link">
              <Button
                category="light"
                icon="plus"
                onClick={handleAddLink}
                size="small"
                text={messages.commonAddLink}
              />
            </div>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 7px;
        }
        .title:not(:first-child) {
          margin-top: 20px;
        }
        .links {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .sublinks {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding-left: 30px;
        }
        .sublinks:not(:empty) {
          margin-top: 10px;
        }
      `}</style>
    </>
  );
};

export default EditorBlockVideoLinks;
